import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChange,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { addProtocol, LngLatBounds, Map, RasterLayerSpecification, RasterSourceSpecification, StyleSpecification } from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { Observable, Subscription } from 'rxjs';
import { ConfigurationModel, MapLayer } from '../../models/configuration.model';
import { ConfigurationService } from '../../../configuration/configuration.service';
import { debounceTime } from 'rxjs/operators';
import { MapContent } from './model/MapContent';
import { MapTools } from '../../tools/MapTools';
import { environment } from '../../../../environments/environment';
import { MapLayersManager } from '../map-viewer/map-layers-manager';
import { EmptyMapContent } from './model/EmptyMapContent';
import { BaseMapType, ISettingKeyValuePair, SettingsService } from '../../../configuration/settings.service';
import { BasemapToggleComponent } from './basemap-toggle/basemap-toggle.component';
import { MapLayerType } from '../../models/map-layer.model';
import { ShiftMapContent } from './model/map-content/ShiftMapContent';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { NgClass } from '@angular/common';

@Component({
  selector: 'app-map-preview',
  templateUrl: './map-preview.component.html',
  styleUrls: ['./map-preview.component.scss'],
  imports: [MatProgressSpinner, BasemapToggleComponent, NgClass],
})
export class MapPreviewComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {
  static readonly MAP_MARKER_IMAGE = 'address-marker-image';
  static readonly TRACK_ARROW_IMAGE = 'track-arrow-image';
  static readonly FLAG_IMAGE = 'flag-image';
  static readonly STAR_IMAGE = 'star-image';
  static readonly CAMERA_IMAGE = 'camera-image';
  static readonly AZIMUTH_IMAGE = 'azimuth-image';

  @Input() nextContent: MapContent;

  @Input() mapTitle = 'preview-map';

  @ViewChild('overlaymap') private mapContainer: ElementRef<HTMLElement>;
  @ViewChild(BasemapToggleComponent) private basemapSwitcher: BasemapToggleComponent;
  map: Map;
  protocol: Protocol;

  private mapLoaded = false;
  loadingContent = true;
  private initialContent: MapContent = null;
  private content: MapContent = null;
  private mapStyle: string | StyleSpecification;
  private baseMap: string;
  private customBaseMapId: string;

  private defaultBounds: LngLatBounds = new LngLatBounds([-105.5, 39], [-105.0, 40]);

  private tenantConfigurationSubscription: Subscription;
  public tenantConfiguration: ConfigurationModel;

  private mapResizeObserver: ResizeObserver;
  private mapResizeObserverSubscription: Subscription;
  private settingsSubscription: Subscription;

  BaseMapType = BaseMapType;

  constructor(
    private configurationService: ConfigurationService,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {
    this.initialContent = this.nextContent == null ? EmptyMapContent.instance : this.nextContent;
    this.tenantConfigurationSubscription = this.configurationService.sharedConfigurationModel.subscribe(tenantConfiguration => {
      if (tenantConfiguration) {
        this.tenantConfiguration = tenantConfiguration;
      }
    });

    this.settingsSubscription = this.settingsService.settingsChangedObservable.subscribe((keyValuePair: ISettingKeyValuePair) => {
      this.onSettingsChanged(keyValuePair.key, keyValuePair.value);
    });
  }

  async ngAfterViewInit() {
    if (!!this.tenantConfiguration) {
      const tenantConfiguration = this.tenantConfiguration;
      if (tenantConfiguration.region.length > 0) {
        this.defaultBounds = tenantConfiguration.region.reduce(
          (bounds, latLngModel) => {
            return bounds.extend([latLngModel.lng, latLngModel.lat]);
          },
          new LngLatBounds(tenantConfiguration.region[0], tenantConfiguration.region[0])
        );
      } else {
        console.warn('region in tenant configuration is empty - leaving default bounds');
      }

      this.baseMap = this.settingsService.getStringValue(SettingsService.BASE_MAP_LAYER_KEY);
      this.customBaseMapId = this.settingsService.getStringValue(SettingsService.CUSTOM_BASE_MAP_KEY);
      this.mapStyle = await this.getStyleFromSettings();
    }

    this.initMap();
    this.subscribeToMapResize();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const nextContentChange = changes['nextContent'] as SimpleChange;
    const nextContent = nextContentChange.currentValue as MapContent;

    if (this.isMapLoaded()) {
      this.loadContent(nextContent);
    } else {
      this.initialContent = nextContent;
    }
  }

  private loadContent(nextContent: MapContent) {
    this.loadingContent = true;
    this.content?.unload();
    this.content = nextContent == null ? EmptyMapContent.instance : nextContent;
    this.content.load(this.map, this.protocol, true);
    this.loadingContent = false;
  }

  ngOnDestroy(): void {
    this.settingsSubscription?.unsubscribe();
    this.content?.unload();
    this.tenantConfigurationSubscription?.unsubscribe();
    this.mapResizeObserver?.disconnect();
    this.mapResizeObserverSubscription?.unsubscribe();
  }

  private subscribeToMapResize() {
    const resizeObservable = new Observable<void>(subscriber => {
      this.mapResizeObserver = new ResizeObserver(entries => {
        entries.forEach(entry => {
          subscriber.next();
        });
      });
    });

    this.mapResizeObserverSubscription = resizeObservable.pipe(debounceTime(200)).subscribe({ next: () => this.map.resize() });

    this.mapResizeObserver.observe(this.mapContainer.nativeElement);
  }

  initMap() {
    this.map = new Map({
      container: this.mapContainer.nativeElement,
      style: !!this.mapStyle ? this.mapStyle : MapLayersManager.ACCUTERRA_WINTER_MAP_STYLE,
      bounds: this.defaultBounds,
      attributionControl: false,
      // center: [-94.66272456393244, 38.973159472910915],
    });

    this.protocol = new Protocol();
    addProtocol('pmtiles', this.protocol.tile);

    this.map.on('load', () => {
      this.addImages();
      const canvasElement = this.map.getCanvas();
      canvasElement.setAttribute('aria-label', this.mapTitle);
      this.mapLoaded = true;
      this.loadContent(this.initialContent);
      this.initialContent = null;
    });

    this.map.on('click', e => {
      this.basemapSwitcher.closeSwitcher();
    });
  }

  private addImages() {
    // source: https://fonts.google.com/icons?selected=Material+Icons:menu
    MapTools.loadImage(
      this.map,
      MapPreviewComponent.MAP_MARKER_IMAGE,
      `${environment.base_href}assets/baseline_location_on_black_24dp.png`,
      { sdf: true }
    );

    MapTools.loadImage(
      this.map,
      MapPreviewComponent.TRACK_ARROW_IMAGE,
      `${environment.base_href}assets/baseline_expand_less_black_24dp.png`,
      { sdf: false }
    );

    MapTools.loadImage(this.map, MapPreviewComponent.FLAG_IMAGE, `${environment.base_href}assets/baseline_flag_black_24dp.png`, {
      sdf: true,
    });

    MapTools.loadImage(this.map, MapPreviewComponent.STAR_IMAGE, `${environment.base_href}assets/star.png`, { sdf: true });

    MapTools.loadImage(this.map, MapPreviewComponent.CAMERA_IMAGE, `${environment.base_href}assets/baseline_photo_camera_black_24dp.png`, {
      sdf: true,
    });

    MapTools.loadImage(this.map, MapPreviewComponent.AZIMUTH_IMAGE, `${environment.base_href}assets/baseline_navigation_black.png`, {
      sdf: true,
    });
  }

  private updateMapStyle() {
    if (!!this.map) {
      this.map.setStyle(this.mapStyle);
      const that = this;
      this.map.once('styledata', data => {
        const waiting = () => {
          if (!that.map.isStyleLoaded()) {
            setTimeout(waiting, 200);
          } else {
            that.onMapStyleChange();
          }
        };
        waiting();
      });
    }
  }

  private isMapLoaded() {
    return this.map != null && this.mapLoaded;
  }

  private async onSettingsChanged(key: string, value: any) {
    switch (key) {
      case SettingsService.BASE_MAP_LAYER_KEY:
        this.baseMap = value;
        this.mapStyle = await this.getStyleFromSettings();
        this.updateMapStyle();
        break;
      case SettingsService.CUSTOM_BASE_MAP_KEY:
        this.customBaseMapId = value;
        this.mapStyle = await this.getStyleFromSettings();
        this.updateMapStyle();
        break;
      default: // other layers in configuration
        break;
    }
  }

  private onMapStyleChange() {
    this.loadingContent = true;
    this.content.load(this.map, this.protocol, false);
    this.loadingContent = false;
  }

  private async getStyleFromSettings() {
    switch (this.baseMap) {
      case BaseMapType.OUTDOORS:
        return this.tenantConfiguration?.useMetricSystem
          ? MapLayersManager.ACCUTERRA_OUTDOORS_M_MAP_STYLE
          : MapLayersManager.ACCUTERRA_OUTDOORS_MAP_STYLE;
      case BaseMapType.DARK:
        return this.tenantConfiguration?.useMetricSystem
          ? MapLayersManager.ACCUTERRA_DARK_M_MAP_STYLE
          : MapLayersManager.ACCUTERRA_DARK_MAP_STYLE;
      case BaseMapType.WINTER:
        return this.tenantConfiguration?.useMetricSystem
          ? MapLayersManager.ACCUTERRA_WINTER_M_MAP_STYLE
          : MapLayersManager.ACCUTERRA_WINTER_MAP_STYLE;
      case BaseMapType.CUSTOM:
        let style = await MapLayersManager.getEmptyStyle(this.tenantConfiguration?.useMetricSystem);
        if (this.tenantConfiguration && this.tenantConfiguration.additionalLayers) {
          const layer = this.tenantConfiguration.additionalLayers.find(l => l.useAsBaseMap && l.name === this.customBaseMapId);
          if (layer) {
            // only raster layer with no credentials set can be used as custom basemap
            style = this.addRasterLayer(style, layer);
          } else {
            console.warn(`Custom BaseMap: Layer ${this.customBaseMapId} not found`);
          }
        }
        return style;
      case BaseMapType.IMAGERY:
      default:
        return this.tenantConfiguration?.useMetricSystem ? MapLayersManager.IMAGERY_M_MAP_STYLE : MapLayersManager.IMAGERY_MAP_STYLE;
    }
  }

  private addRasterLayer(style: StyleSpecification, layerInfo: MapLayer): StyleSpecification {
    if (layerInfo.type !== MapLayerType.RASTER) {
      console.warn('Invalid Custom BaseMap type');
      return style;
    }
    const layer = {
      id: layerInfo.name,
      source: `${layerInfo.name}-source`,
      type: 'raster',
      layout: {
        visibility: 'visible',
      },
      paint: {
        'raster-opacity': layerInfo.opacity !== undefined && layerInfo.opacity !== null ? layerInfo.opacity : 1.0,
      },
    } as RasterLayerSpecification;

    const source = {
      type: 'raster',
      tiles: [layerInfo.url],
      tileSize: 256,
    } as RasterSourceSpecification;
    style.sources[`${layerInfo.name}-source`] = source;
    style.layers = [layer, ...style.layers];
    return style;
  }

  showBaseMapToggleOnTop(): boolean {
    return this.nextContent instanceof ShiftMapContent;
  }
}
